/**
 * AI Art Director — the reviewer.
 *
 * Receives ONLY: Creative Plan + Research Brief + selected Library Patterns +
 * Style Genome + Generation Constraints. Reviews CREATIVE quality the way a
 * creative director does — intent, narrative, language, craft, originality,
 * marketplace value — and returns a verdict.
 *
 * It never sees canvas, pixels, geometry, Fabric, the editor, the compiler, or
 * objects. It never rewrites the design and never produces Design JSON: when it
 * rejects, it returns structured revision requests and hands the work back.
 *
 * The Thinking Engine creates. The Art Director reviews. Only an approved plan
 * may go to OpenAI.
 */

import {
  REVIEW_SCHEMA_ID, REVIEW_VERSION, DEFAULT_THRESHOLDS, SEVERITY, SEVERITY_ORDER,
} from './reviewSchema';
import { RULES } from './rules';
import { computeScores, computeOverallConfidence } from './score';

const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

/** Normalise inputs so a rule never has to guard. */
const buildContext = (inputs) => ({
  plan: asObject(inputs.plan),
  research: asObject(inputs.research),
  patterns: Array.isArray(inputs.patterns) ? inputs.patterns.filter((p) => p && typeof p === 'object') : [],
  genome: asObject(inputs.genome),
  constraints: asObject(inputs.constraints),
});

/** A one-line account of the verdict, for humans reading the review. */
const summarize = (approved, scores, findings, strengths) => {
  const blockers = findings.filter((f) => f.severity === SEVERITY.BLOCKER).length;
  const verdict = approved ? 'Approved' : 'Rejected';
  const issues = findings.length === 0
    ? 'no creative issues'
    : `${findings.length} creative issue${findings.length === 1 ? '' : 's'}${blockers ? ` (${blockers} blocking)` : ''}`;
  return `${verdict} — creative ${scores.creative}, marketplace ${scores.marketplace}, premium ${scores.premium}, originality ${scores.originality}. ${issues}; ${strengths.length} strength${strengths.length === 1 ? '' : 's'}.`;
};

/**
 * Review a Creative Plan.
 *
 * @param {Object} inputs
 * @param {Object} inputs.plan         Creative Plan (Phase 6).
 * @param {Object} inputs.research     Research Brief (Phase 4).
 * @param {Object[]} inputs.patterns   Selected Library Patterns (Phase 7).
 * @param {Object} inputs.genome       Style Genome (Phase 3).
 * @param {Object} [inputs.constraints] Generation Constraints (Phase 5).
 * @param {Object} [options]
 * @param {Object} [options.thresholds] { creative, marketplace }.
 * @returns {Object} the Creative Review.
 */
export const reviewCreativePlan = (inputs = {}, options = {}) => {
  const ctx = buildContext(asObject(inputs));
  const thresholds = { ...DEFAULT_THRESHOLDS, ...asObject(options.thresholds) };

  const findings = [];
  const strengths = [];

  RULES.forEach((rule) => {
    const result = asObject(rule.check(ctx));
    if (result.issue) {
      findings.push({
        rule: rule.id,
        reviews: rule.reviews,
        detects: rule.detects,
        severity: rule.severity,
        scores: rule.scores,
        message: rule.weakness(ctx, result.detail),
        request: rule.revision(ctx, result.detail),
      });
      return;
    }
    if (rule.strength) {
      const note = rule.strength(ctx);
      if (note) strengths.push(note);
    }
  });

  // Most urgent first; ties keep rule order, so the review is stable.
  const ranked = [...findings].sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));

  const scores = computeScores(findings);
  const overallConfidence = computeOverallConfidence(scores, ctx.plan, ctx.research);
  const blocking = findings.some((f) => f.severity === SEVERITY.BLOCKER);
  const approved = !blocking && scores.creative >= thresholds.creative && scores.marketplace >= thresholds.marketplace;

  return {
    schema: REVIEW_SCHEMA_ID,
    version: REVIEW_VERSION,
    approved,
    rejected: !approved,
    review: summarize(approved, scores, findings, strengths),
    scores,
    overallConfidence,
    strengths,
    weaknesses: ranked.map(({ rule, reviews, detects, severity, message }) => ({ rule, reviews, detects, severity, message })),
    // On rejection these are the whole answer: the Art Director asks for revision, it does not revise.
    revisionRequests: approved
      ? []
      : ranked.map(({ rule, reviews, detects, severity, request }) => ({ rule, areas: reviews, detects, severity, request })),
    meta: {
      genome: ctx.genome.id || null,
      patternCount: ctx.patterns.length,
      rulesRun: RULES.length,
      thresholds,
    },
  };
};
